import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { getBatchUpdateUrl, IDirSyncStatusAndLanguagePreference } from '../../redux/modules/graphApis';
import { ILanguageEntry, ILanguageUpdateEntry, ISetDisplayLanguageBatchPayload, LanguageActions } from '../models';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import {
  IRegionalAndLanguageSettingsQueryResult,
  useGetRegionalAndLanguageSettingsQuery,
} from './useGetRegionalAndLanguageSettingQuery';
import { useGetAvailableLocalesQuery } from './useGetAvailableLocalesQuery';
import { isHttpSuccess } from '../utilities/isHttpSuccess';
import { getI18n } from 'react-i18next';
import { getTelemetry } from '../../Telemetry/Telemetry';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { useGetLanguagePreferenceQuery } from './useGetLanguagePreferenceQuery';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export const useSetDisplayLanguageBatchQuery = (): UseMutationResult<
  ISetDisplayLanguageBatchPayload | undefined,
  AnyError,
  ILanguageEntry,
  unknown
> => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const availableLocalesQuery = useGetAvailableLocalesQuery();
  const dlpQuery = useGetLanguagePreferenceQuery();

  const { authoringLanguages, regionalFormatOverrides, isMailboxAvailable } = useGetRegionalAndLanguageSettingsQuery();
  const { startBatchTelemetryReport, stopBatchTelemetryReport, stopTelemetryReport } =
    useReportAPITelemetry('setDisplayLanguage');

  return useMutation(async (language: ILanguageEntry) => {
    const selectedLanguageTag = language.bcp47Tag;
    const availableLocales = availableLocalesQuery.data || [];
    let newAuthoringLanguages = authoringLanguages;

    if (authoringLanguages.findIndex((item) => item.toLowerCase() === selectedLanguageTag.toLowerCase()) === -1) {
      newAuthoringLanguages = [selectedLanguageTag, ...newAuthoringLanguages];
    }

    const updateAuthoringLanguages: ILanguageUpdateEntry[] = newAuthoringLanguages.map((item) => {
      return { locale: item };
    });

    const url = getBatchUpdateUrl();
    const telemetry = getTelemetry();

    const regionalFormat = availableLocales.find((element) => element.locale === selectedLanguageTag);
    if (!regionalFormat) {
      telemetry.error(LanguageActions.SetDisplayLanguageFailed, {
        error: `${selectedLanguageTag} is not found in supported regional formats`,
      });
      return;
    }

    const requests = [];
    const scopes = [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope];
    if (!dlpQuery.onPremisesSyncEnabled) {
      requests.push({
        id: '1',
        method: 'PATCH',
        url: '/me/preferredLanguage',
        body: {
          value: selectedLanguageTag,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    requests.push({
      id: '2',
      method: 'PATCH',
      url: '/me/settings/regionalAndLanguageSettings',
      body: {
        authoringLanguages: updateAuthoringLanguages,
        defaultDisplayLanguage: {
          locale: selectedLanguageTag,
        },
        regionalFormatOverrides: regionalFormatOverrides,
        defaultRegionalFormat: {
          locale: regionalFormat.locale,
        },
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (isMailboxAvailable) {
      requests.push({
        id: '3',
        method: 'PATCH',
        url: '/me/mailboxSettings',
        body: {
          language: {
            locale: selectedLanguageTag,
          },
          dateFormat: regionalFormatOverrides.shortDateFormat,
          timeFormat: regionalFormatOverrides.shortTimeFormat,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });
      scopes.push(getMSGraphScopeAndEndPoint(MSGraphScopes.MailboxSettingsReadWrite).scope);
    }

    const token = await auth.acquireToken({
      scopes,
    });

    startBatchTelemetryReport(requests);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        requests,
      }),
    });

    if (!response.ok) {
      const error = new HttpRequestError(response.status, response.statusText);
      stopTelemetryReport('error', error);
      throw error;
    }

    const data = (await response.json()) as ISetDisplayLanguageBatchPayload;
    const responses = data.responses;

    responses.forEach((item) => {
      stopBatchTelemetryReport(item.id, item);
    });

    const isSetDisplayLanguageSucceeded: boolean = isHttpSuccess(responses[0].status);
    const isUpdateSettingsSucceeded: boolean = responses.length > 1 && isHttpSuccess(responses[1].status);

    if (responses && isSetDisplayLanguageSucceeded) {
      getI18n().changeLanguage(selectedLanguageTag);
    }

    queryClient.setQueryData<IDirSyncStatusAndLanguagePreference | undefined>(
      QueryKey.SYNC_STATUS_AND_LANGUAGE_PREFERENCE,
      (old) => {
        if (old && isSetDisplayLanguageSucceeded) {
          return {
            ...old,
            preferredLanguage: selectedLanguageTag,
          };
        }
        return old;
      },
    );

    queryClient.setQueryData<IRegionalAndLanguageSettingsQueryResult | undefined>(
      QueryKey.REGIONAL_AND_LANGUAGE_SETTINGS,
      (old) => {
        if (old && isUpdateSettingsSucceeded) {
          return {
            ...old,
            authoringLanguages: newAuthoringLanguages,
            defaultDisplayLanguage: selectedLanguageTag,
            defaultRegionalFormat: regionalFormat,
            regionalFormatOverrides: regionalFormatOverrides,
          };
        }
        return old;
      },
    );
    queryClient.invalidateQueries(QueryKey.TIMEZONE);
    return data;
  });
};
